import { uuidv7 } from 'uuidv7';
import type {
  PushPlatformFamily,
  PushPermissionState,
  UpsertPushSubscriptionRequest,
} from '@/contracts';
import { UuidSchema } from '@/contracts/uuid';

const PUSH_APP_INSTALL_ID_STORAGE_KEY = 'dwellbot_push_app_install_id';

export type BrowserPushPermissionState = PushPermissionState | 'unsupported';

type BrowserPushSubscriptionKeys = {
  p256dh: string;
  auth: string;
};

type BuildUpsertPushSubscriptionRequestInput = {
  appInstallId: string;
  subscription: PushSubscription;
  permissionState: PushPermissionState;
  platformFamily: PushPlatformFamily;
  deviceLabel: string | null;
};

export const getOrCreatePushAppInstallId = (): string => {
  const storedAppInstallId = window.localStorage.getItem(
    PUSH_APP_INSTALL_ID_STORAGE_KEY
  );
  const parsedAppInstallId = UuidSchema.safeParse(storedAppInstallId);
  if (parsedAppInstallId.success) {
    return parsedAppInstallId.data;
  }

  const createdAppInstallId = uuidv7();
  window.localStorage.setItem(
    PUSH_APP_INSTALL_ID_STORAGE_KEY,
    createdAppInstallId
  );
  return createdAppInstallId;
};

export const isWebPushSupported = (): boolean => {
  const hasNotificationApi = typeof Notification !== 'undefined';
  const hasServiceWorkerApi = 'serviceWorker' in navigator;
  const hasPushManagerApi = typeof PushManager !== 'undefined';
  return hasNotificationApi && hasServiceWorkerApi && hasPushManagerApi;
};

export const readBrowserPushPermissionState = (): BrowserPushPermissionState => {
  if (typeof Notification === 'undefined') {
    return 'unsupported';
  }

  return Notification.permission;
};

export const requestBrowserPushPermission =
  async (): Promise<BrowserPushPermissionState> => {
    if (typeof Notification === 'undefined') {
      return 'unsupported';
    }

    const nextPermissionState = await Notification.requestPermission();
    return nextPermissionState;
  };

const encodePushKey = (buffer: ArrayBuffer | null): string => {
  if (buffer === null) {
    throw new Error('Push subscription is missing a required key.');
  }

  const bufferBytes = new Uint8Array(buffer);
  let binaryValue = '';
  for (const byte of bufferBytes) {
    binaryValue += String.fromCharCode(byte);
  }

  const encodedValue = window.btoa(binaryValue);
  return encodedValue;
};

const extractPushSubscriptionKeys = (
  subscription: PushSubscription
): BrowserPushSubscriptionKeys => {
  const p256dhKey = subscription.getKey('p256dh');
  const authKey = subscription.getKey('auth');

  return {
    p256dh: encodePushKey(p256dhKey),
    auth: encodePushKey(authKey),
  };
};

const decodeBase64Url = (value: string): Uint8Array => {
  const normalizedValue = value.padEnd(
    value.length + ((4 - (value.length % 4)) % 4),
    '='
  );
  const base64Value = normalizedValue.replace(/-/g, '+').replace(/_/g, '/');
  const binaryValue = window.atob(base64Value);
  const binaryLength = binaryValue.length;
  const bytes = new Uint8Array(binaryLength);

  for (let index = 0; index < binaryLength; index += 1) {
    bytes[index] = binaryValue.charCodeAt(index);
  }

  return bytes;
};

export const ensureBrowserPushSubscription = async (
  vapidPublicKey: string
): Promise<PushSubscription> => {
  const serviceWorkerRegistration = await navigator.serviceWorker.ready;
  const existingSubscription =
    await serviceWorkerRegistration.pushManager.getSubscription();
  if (existingSubscription !== null) {
    return existingSubscription;
  }

  const applicationServerKey = decodeBase64Url(vapidPublicKey);
  const createdSubscription =
    await serviceWorkerRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  return createdSubscription;
};

export const readBrowserPushSubscription = async (): Promise<PushSubscription | null> => {
  const serviceWorkerRegistration = await navigator.serviceWorker.ready;
  const pushSubscription =
    await serviceWorkerRegistration.pushManager.getSubscription();
  return pushSubscription;
};

export const resolvePushDeviceLabel = (
  platformFamily: PushPlatformFamily
): string => {
  if (platformFamily === 'ios') {
    return 'iPhone Home Screen';
  }

  if (platformFamily === 'android') {
    return 'Android Web App';
  }

  if (platformFamily === 'desktop') {
    return 'Desktop Browser';
  }

  return 'Browser Device';
};

export const buildUpsertPushSubscriptionRequest = ({
  appInstallId,
  subscription,
  permissionState,
  platformFamily,
  deviceLabel,
}: BuildUpsertPushSubscriptionRequestInput): UpsertPushSubscriptionRequest => {
  const keys = extractPushSubscriptionKeys(subscription);
  const requestPayload: UpsertPushSubscriptionRequest = {
    app_install_id: appInstallId,
    endpoint: subscription.endpoint,
    keys,
    permission_state: permissionState,
    platform_family: platformFamily,
    device_label: deviceLabel ?? undefined,
  };
  return requestPayload;
};
